import { useNavigate } from 'react-router-dom';
import { useSnackbar } from '@fdha/web-ui-library';
import {
  useGetMenuOptionSelectionsV2Query,
  useGetSelectedMenuOptionsV2Query,
  useUpdatePatientMealSelectionMutation,
  useGetDishQuery,
  useGetMealKitQuery,
  MealSelections,
  MenuOptionSelection,
  MealOptionType,
  DeliveryStatus,
  GetMenuOptionSelectionsV2Query,
} from '@fdha/graphql-api-admin';
import type {
  IPlatformBridge,
  ChooseMealsParams,
  ClosedDeliveryMenuOptions,
} from '@fdha/common-hooks';
import { aggregateMealKitVariants } from '@fdha/common-utils';

class WebPlatformBridge implements IPlatformBridge {
  private navigate: ReturnType<typeof useNavigate>;
  private showSnackbar: ReturnType<typeof useSnackbar>['showSnackbar'];

  // Platform identification
  readonly isAdminPlatform = true;

  constructor(
    navigate: ReturnType<typeof useNavigate>,
    showSnackbar: ReturnType<typeof useSnackbar>['showSnackbar']
  ) {
    this.navigate = navigate;
    this.showSnackbar = showSnackbar;
  }

  // Navigation
  navigateToChooseMeals(params: ChooseMealsParams): void {
    // This would navigate to the Choose Meals screen when implemented
    // Example: this.navigate('/choose-meals', { state: params });
  }

  goBack(): void {
    this.navigate(-1);
  }

  // Notifications
  showSuccess(message: string): void {
    this.showSnackbar({
      message,
      severity: 'success',
    });
  }

  showError(message: string): void {
    this.showSnackbar({
      message,
      severity: 'error',
    });
  }

  // GraphQL hooks - direct assignment (interface uses Function types)
  useGetMenuOptionSelectionsQuery = useGetMenuOptionSelectionsV2Query;
  useGetMenuOptionSelectionsV2Query = useGetMenuOptionSelectionsV2Query;
  useGetSelectedMenuOptionsV2Query = useGetSelectedMenuOptionsV2Query;
  useUpdatePatientMealSelectionV2Mutation =
    useUpdatePatientMealSelectionMutation;
  useGetDishQuery = useGetDishQuery;
  useGetMealKitQuery = useGetMealKitQuery;

  // GraphQL types
  MealSelections = {} as MealSelections;
  MenuOptionSelection = {} as MenuOptionSelection;
  MealOptionType = MealOptionType;
  DeliveryStatus = DeliveryStatus;

  // Platform-specific utilities
  aggregateMealKitVariants = (items: MenuOptionSelection[]) =>
    aggregateMealKitVariants(items, 'web');

  // Platform-specific query variable builder
  buildQueryVariables(
    patientId: string | null,
    dietPlanDeliveryMenuId: string | null
  ) {
    // Admin API uses both patientId and dietPlanDeliveryMenuId
    return {
      patientId: patientId || '',
      dietPlanDeliveryMenuId: dietPlanDeliveryMenuId || '',
    };
  }

  // Platform-specific response data extractor
  extractMenuOptionsData(data: GetMenuOptionSelectionsV2Query) {
    // Admin API returns data.getMenuOptionSelectionsV2
    return data?.getMenuOptionSelectionsV2;
  }

  // Platform-specific closed delivery query variable builder
  buildClosedDeliveryQueryVariables(deliveryId: string) {
    return { deliveryId };
  }

  // Platform-specific closed delivery response data extractor
  extractClosedDeliveryData(data: { selectedMenuOptionsV2?: unknown }) {
    // Admin API returns data.selectedMenuOptionsV2
    return data?.selectedMenuOptionsV2 as ClosedDeliveryMenuOptions;
  }
}

// Hook to create the bridge instance
export const useWebPlatformBridge = (): IPlatformBridge => {
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();

  return new WebPlatformBridge(navigate, showSnackbar);
};
